import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence, motion } from 'framer-motion';

// Context
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import ScrollToTop from './components/ScrollToTop';

// Firebase
import './firebase';

// Pages
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import ThankYou from './pages/ThankYou';
import Logos from './pages/Logos';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Health Section
import HealthLayout from './components/health/HealthLayout';

// Scroll to top on route change
const ScrollToTopOnRouteChange = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return null;
};

// Page transition wrapper
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ 
        duration: 0.4,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );
};

// Layout component
const Layout = ({ children }) => {
  const location = useLocation();
  
  // Hide navbar and footer on thank you page for focused experience
  const isThankYouPage = location.pathname === '/thank-you';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F0E8', color: '#1A1A1A' }}>
      {!isThankYouPage && <Navbar />}
      
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={location.pathname}>
          <PageTransition>
            {children}
          </PageTransition>
        </motion.div>
      </AnimatePresence>
      
      {!isThankYouPage && (
        <>
          <Footer />
          <FloatingWhatsApp />
          <ScrollToTop />
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <Router>
          <ScrollToTopOnRouteChange />
          <Layout>
            <Routes>
              {/* Main Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/logos" element={<Logos />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Health Section */}
              <Route path="/health/*" element={<HealthLayout />} />

              {/* Catch all route - redirect to home */}
              <Route path="*" element={<Home />} />
            </Routes>
          </Layout>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;